import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import sitePlanImage from "../assets/site-plan.jpg";
import { Save, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_COLORS = {
  available: { bg: "#0B1A0F", ring: "#22c55e", text: "#B8E07A" },
  reserved: { bg: "#0B1A0F", ring: "#F1C36B", text: "#F1C36B" },
  sold: { bg: "#0B1A0F", ring: "#9CA3AF", text: "#D1D5DB" },
};

/**
 * Visual editor for lot pin positions on the survey map.
 * Drag pins to relocate; save persists map_x and map_y to the backend.
 */
export default function SitePlanEditor({ properties, onSaved }) {
  const [positions, setPositions] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [draggingId, setDraggingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState({}); // local status edits
  const mapRef = useRef(null);

  useEffect(() => {
    const init = {};
    const stat = {};
    properties.forEach((p) => {
      init[p.id] = { x: p.map_x ?? 50, y: p.map_y ?? 50 };
      stat[p.id] = p.status || "available";
    });
    setPositions(init);
    setStatusFilter(stat);
    setDirty(new Set());
  }, [properties]);

  const markDirty = (id) => setDirty((d) => new Set([...d, id]));

  const handlePointerDown = (e, id) => {
    e.preventDefault();
    setDraggingId(id);
  };

  useEffect(() => {
    if (!draggingId) return;
    const move = (e) => {
      const rect = mapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      const clientY = e.clientY ?? e.touches?.[0]?.clientY;
      if (clientX == null) return;
      const x = ((clientX - rect.left) / rect.width) * 100;
      const y = ((clientY - rect.top) / rect.height) * 100;
      setPositions((p) => ({
        ...p,
        [draggingId]: {
          x: Math.max(0, Math.min(100, x)),
          y: Math.max(0, Math.min(100, y)),
        },
      }));
      markDirty(draggingId);
    };
    const up = () => setDraggingId(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("pointerup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("touchend", up);
    };
  }, [draggingId]);

  const saveAll = async () => {
    const ids = Array.from(dirty);
    if (ids.length === 0) {
      toast.info("Nada que guardar — mueve algún pin primero");
      return;
    }
    try {
      await Promise.all(
        ids.map((id) =>
          api.put(`/properties/${id}`, {
            map_x: Number(positions[id].x.toFixed(2)),
            map_y: Number(positions[id].y.toFixed(2)),
            status: statusFilter[id],
          })
        )
      );
      toast.success(`Guardadas ${ids.length} posiciones`);
      setDirty(new Set());
      onSaved?.();
    } catch {
      toast.error("No se pudieron guardar los cambios");
    }
  };

  const resetLot = (id) => {
    const p = properties.find((x) => x.id === id);
    if (!p) return;
    setPositions((s) => ({ ...s, [id]: { x: p.map_x ?? 50, y: p.map_y ?? 50 } }));
    setStatusFilter((s) => ({ ...s, [id]: p.status || "available" }));
    setDirty((d) => {
      const n = new Set(d);
      n.delete(id);
      return n;
    });
  };

  const changeStatus = (id, status) => {
    setStatusFilter((s) => ({ ...s, [id]: status }));
    markDirty(id);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-3xl text-[var(--c-text)]">Editor del mapa</h2>
          <p className="text-sm text-[var(--c-text)]/70 mt-1">
            Arrastra cualquier número sobre el plano para moverlo. Cambia el estado abajo. Luego guarda.
          </p>
        </div>
        <Button
          data-testid="save-map-positions"
          onClick={saveAll}
          disabled={dirty.size === 0}
          className="bg-gradient-to-r from-[var(--c-firefly-gold)] to-[var(--c-bio-green)] hover:opacity-90 text-[#0B1A0F] font-semibold rounded-full disabled:opacity-40"
        >
          <Save className="w-4 h-4 mr-1.5" /> Guardar {dirty.size > 0 ? `(${dirty.size})` : ""}
        </Button>
      </div>

      {/* Map editor */}
      <div
        ref={mapRef}
        className="relative rounded-2xl overflow-hidden border-2 border-[var(--c-bio-green)]/25 mx-auto select-none touch-none shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]"
        style={{ aspectRatio: "916 / 572", background: "#FAFAF6", maxWidth: "1100px", containerType: "inline-size" }}
      >
        <img
          src={sitePlanImage}
          alt="Plano"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          draggable="false"
        />
        {properties.map((p) => {
          const pos = positions[p.id] || { x: p.map_x ?? 50, y: p.map_y ?? 50 };
          const status = statusFilter[p.id] || p.status || "available";
          const s = STATUS_COLORS[status] || STATUS_COLORS.available;
          const isDirty = dirty.has(p.id);
          return (
            <button
              key={p.id}
              type="button"
              onPointerDown={(e) => handlePointerDown(e, p.id)}
              data-testid={`editor-pin-${p.lot_number}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 cursor-move focus:outline-none touch-none"
              style={{ left: `${pos.x}%`, top: `${pos.y}%`, zIndex: draggingId === p.id ? 50 : 10 }}
              title={`Lote #${p.lot_number} · ${p.name}`}
            >
              <span
                className="block rounded-full flex items-center justify-center font-serif font-bold"
                style={{
                  width: "clamp(28px, 4.5cqw, 52px)",
                  height: "clamp(28px, 4.5cqw, 52px)",
                  fontSize: "clamp(12px, 1.8cqw, 20px)",
                  background: s.bg,
                  color: s.text,
                  border: `3px solid ${isDirty ? "#F1C36B" : s.ring}`,
                  boxShadow: isDirty
                    ? "0 0 0 4px rgba(241,195,107,0.4), 0 8px 24px rgba(0,0,0,0.5)"
                    : "0 4px 14px rgba(0,0,0,0.4)",
                }}
              >
                {p.lot_number}
              </span>
            </button>
          );
        })}
      </div>

      {/* Lot status grid */}
      <div className="rounded-2xl border border-[var(--c-bio-green)]/15 bg-[var(--c-bg-forest)]/60 backdrop-blur overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--c-bio-green)]/15 bg-[var(--c-bg-deep)]/50 text-xs uppercase tracking-widest text-[var(--c-firefly-gold)] font-medium">
          Estado y coordenadas de cada lote
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 lg:divide-x divide-[var(--c-bio-green)]/10">
          {properties.map((p) => {
            const pos = positions[p.id] || { x: p.map_x ?? 50, y: p.map_y ?? 50 };
            const status = statusFilter[p.id] || p.status || "available";
            const isDirty = dirty.has(p.id);
            return (
              <div
                key={p.id}
                data-testid={`lot-row-${p.lot_number}`}
                className={`p-3 flex items-center gap-3 border-b border-[var(--c-bio-green)]/10 sm:border-b ${
                  isDirty ? "bg-[var(--c-firefly-gold)]/10" : "bg-transparent"
                }`}
              >
                <span
                  className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-serif font-bold text-sm"
                  style={{
                    background: STATUS_COLORS[status].bg,
                    color: STATUS_COLORS[status].text,
                    border: `2px solid ${STATUS_COLORS[status].ring}`,
                  }}
                >
                  {p.lot_number}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[var(--c-text)] text-sm truncate">{p.name}</div>
                  <div className="text-[10px] text-[var(--c-text)]/55 font-mono">
                    x: {pos.x.toFixed(1)}% · y: {pos.y.toFixed(1)}%
                  </div>
                </div>
                <select
                  value={status}
                  onChange={(e) => changeStatus(p.id, e.target.value)}
                  data-testid={`status-${p.lot_number}`}
                  className="text-xs rounded-md border border-[var(--c-bio-green)]/30 bg-[var(--c-bg-deep)] px-2 py-1.5 text-[var(--c-text)] focus:outline-none focus:ring-2 focus:ring-[var(--c-firefly-gold)]/40"
                >
                  <option value="available">Disponible</option>
                  <option value="reserved">Reservado</option>
                  <option value="sold">Vendido</option>
                </select>
                <button
                  type="button"
                  onClick={() => resetLot(p.id)}
                  disabled={!isDirty}
                  className="p-1.5 rounded-md text-[var(--c-text)]/55 hover:text-[var(--c-firefly-gold)] hover:bg-[var(--c-bg-deep)] disabled:opacity-25 disabled:cursor-not-allowed"
                  title="Deshacer"
                  data-testid={`reset-${p.lot_number}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
